/**
 * Web chat tool-card for the connector READ tools (M3.2 / M3.3):
 *
 * - listConnectedServices   → connected services with a status dot.
 * - getRecentEmails         → cached recent emails (subject + sender).
 * - getRecentSlackMessages  → cached recent Slack messages (text + channel·user).
 *
 * Web-only (a `.native.tsx` sibling renders iOS/Android). All copy comes from
 * the `connectors.card.*` i18n block. Reads are defensive: the backend result
 * shape is not strongly typed here, so we probe a few field names per item.
 */
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { CardShell } from './card-shell';
import { asArray, safeArray, safeGet } from './types';
import type { ToolCardProps } from './types';

const MAX_DISPLAY = 6;

type ListMode = 'services' | 'emails' | 'messages';

interface ListItem {
  primary: string;
  secondary?: string;
  /** services only: drives the status dot + label */
  connected?: boolean;
}

function modeFor(toolName: string): ListMode {
  if (toolName === 'getRecentEmails') return 'emails';
  if (toolName === 'getRecentSlackMessages') return 'messages';
  return 'services';
}

/** First non-empty array among the candidate keys, else the result-as-array. */
function pickArray(result: unknown, ...keys: string[]): Record<string, unknown>[] {
  for (const key of keys) {
    const arr = safeArray<Record<string, unknown>>(result, key);
    if (arr.length > 0) return arr;
  }
  return asArray<Record<string, unknown>>(result);
}

function extractItems(mode: ListMode, result: unknown): ListItem[] {
  if (mode === 'emails') {
    return pickArray(result, 'emails', 'items', 'messages').map((it) => ({
      primary:
        safeGet<string>(it, 'subject') ||
        safeGet<string>(it, 'title') ||
        safeGet<string>(it, 'snippet') ||
        'N/A',
      secondary:
        safeGet<string>(it, 'from') ||
        safeGet<string>(it, 'sender') ||
        safeGet<string>(it, 'fromName') ||
        undefined,
    }));
  }
  if (mode === 'messages') {
    return pickArray(result, 'messages', 'items').map((it) => {
      const channel = safeGet<string>(it, 'channel') || safeGet<string>(it, 'channelName');
      const user =
        safeGet<string>(it, 'user') ||
        safeGet<string>(it, 'username') ||
        safeGet<string>(it, 'from');
      const meta = [channel, user].filter(Boolean).join(' · ');
      return {
        primary:
          safeGet<string>(it, 'text') ||
          safeGet<string>(it, 'message') ||
          safeGet<string>(it, 'body') ||
          'N/A',
        secondary: meta || undefined,
      };
    });
  }
  return pickArray(result, 'services', 'connections', 'items').map((it) => {
    const status = (
      safeGet<string>(it, 'status') ||
      safeGet<string>(it, 'state') ||
      ''
    ).toLowerCase();
    return {
      primary:
        safeGet<string>(it, 'name') ||
        safeGet<string>(it, 'connector') ||
        safeGet<string>(it, 'slug') ||
        safeGet<string>(it, 'service') ||
        'N/A',
      secondary: safeGet<string>(it, 'description') || undefined,
      connected: status === 'active' || status === 'connected',
    };
  });
}

const ICON_FOR_MODE: Record<ListMode, string> = {
  services: 'hub',
  emails: 'mail',
  messages: 'chat',
};

const LABEL_KEY_FOR_MODE: Record<ListMode, string> = {
  services: 'connectors.card.connectedLabel',
  emails: 'connectors.card.emailsLabel',
  messages: 'connectors.card.messagesLabel',
};

export const ConnectorListCard: FC<ToolCardProps> = ({ toolName, result }) => {
  const { t } = useTranslation('common');
  const mode = modeFor(toolName);
  const all = extractItems(mode, result);
  const items = all.slice(0, MAX_DISPLAY);
  const remaining = all.length - items.length;

  return (
    <CardShell>
      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MaterialSymbol
            name={ICON_FOR_MODE[mode]}
            size={15}
            style={{ color: 'var(--text-tertiary)' }}
          />
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>
            {t(LABEL_KEY_FOR_MODE[mode])}
          </span>
        </div>

        {items.length === 0 ? (
          <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
            {t('connectors.card.listEmpty')}
          </span>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {items.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  padding: '10px 0',
                  borderTop: index > 0 ? '1px solid var(--input-border)' : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  {mode === 'services' ? (
                    <span
                      aria-hidden
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        flexShrink: 0,
                        background: item.connected ? 'var(--text-primary)' : 'transparent',
                        border: item.connected ? 'none' : '1.5px solid var(--text-tertiary)',
                      }}
                    />
                  ) : null}
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'var(--text-primary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    dir="auto"
                  >
                    {item.primary}
                  </span>
                  {mode === 'services' ? (
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)', flexShrink: 0 }}>
                      {item.connected
                        ? t('connectors.status.connected')
                        : t('connectors.status.notConnected')}
                    </span>
                  ) : null}
                </div>
                {item.secondary ? (
                  <span
                    style={{
                      fontSize: 13,
                      lineHeight: '18px',
                      color: 'var(--text-secondary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      paddingInlineStart: mode === 'services' ? 16 : 0,
                    }}
                    dir="auto"
                  >
                    {item.secondary}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        )}

        {remaining > 0 ? (
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            {t('connectors.card.more', { count: remaining })}
          </span>
        ) : null}
      </div>
    </CardShell>
  );
};
